import request from './request'

/**
 * 上传单个图片文件
 * @param {File} file 图片文件（支持jpg、png、gif等格式，最大10MB）
 * @returns {Promise<string>} 返回图片URL
 */
export function uploadImage(file) {
  if (!file || !(file instanceof File)) {
    return Promise.reject(new Error('请选择有效的图片文件'))
  }
  
  const formData = new FormData()
  formData.append('file', file)
  
  // 不设置 Content-Type，让 axios 自动处理 multipart/form-data 和 boundary
  return request.post('/api/admin/cos/upload/image', formData).then(response => {
    // 提取图片URL
    if (response.data && response.data.success && response.data.data) {
      return response.data.data
    }
    throw new Error('上传失败：响应数据格式错误')
  })
}

/**
 * 批量上传图片文件
 * @param {File[]} files 图片文件列表（每个文件最大10MB）
 * @returns {Promise<string[]>} 返回图片URL数组
 */
export function uploadImages(files) {
  if (!Array.isArray(files) || files.length === 0) {
    return Promise.reject(new Error('请选择至少一个图片文件'))
  }
  
  const formData = new FormData()
  // 批量上传时，后端期望files字段为数组
  files.forEach(file => {
    if (file instanceof File) {
      formData.append('files', file)
    }
  })
  
  // 不设置 Content-Type，让 axios 自动处理 multipart/form-data 和 boundary
  return request.post('/api/admin/cos/upload/images', formData).then(response => {
    // 提取图片URL数组
    if (response.data && response.data.success && Array.isArray(response.data.data)) {
      return response.data.data
    }
    throw new Error('上传失败：响应数据格式错误')
  })
}

/**
 * 上传单个视频文件或压缩包
 * @param {File} file 视频文件（支持mp4、avi、mov等格式，最大500MB）或压缩包（zip、rar、7z等，最大2GB）
 * @returns {Promise} 返回上传结果对象 {url, originalFilename, fileType, extension, fileSize, success, errorMessage}
 */
export function uploadVideo(file) {
  if (!file || !(file instanceof File)) {
    return Promise.reject(new Error('请选择有效的文件'))
  }
  
  const formData = new FormData()
  formData.append('file', file)
  
  // 不设置 Content-Type，让 axios 自动处理 multipart/form-data 和 boundary
  return request.post('/api/admin/cos/upload/video', formData).then(response => {
    // 返回完整的上传结果对象
    if (response.data && response.data.success && response.data.data) {
      return response.data.data
    }
    // 如果结构不对，返回整个响应以便调试
    return response.data
  })
}

/**
 * 批量上传视频文件或压缩包
 * @param {File[]} files 视频文件或压缩包列表（视频最大500MB，压缩包最大2GB，单次最多10个文件）
 * @returns {Promise} 返回批量上传结果对象 {totalCount, successCount, failureCount, results, urls}
 */
export function uploadVideos(files) {
  if (!Array.isArray(files) || files.length === 0) {
    return Promise.reject(new Error('请选择至少一个文件'))
  }
  
  if (files.length > 10) {
    return Promise.reject(new Error('单次最多上传10个文件'))
  }
  
  const formData = new FormData()
  // 批量上传时，后端期望files字段为数组
  files.forEach(file => {
    if (file instanceof File) {
      formData.append('files', file)
    }
  })
  
  // 不设置 Content-Type，让 axios 自动处理 multipart/form-data 和 boundary
  return request.post('/api/admin/cos/upload/videos', formData).then(response => {
    // 返回完整的批量上传结果对象
    if (response.data && response.data.success && response.data.data) {
      return response.data.data
    }
    // 如果结构不对，返回整个响应以便调试
    return response.data
  })
}

/**
 * 删除COS中的文件
 * @param {string} fileUrl 文件URL
 * @returns {Promise}
 */
export function deleteFile(fileUrl) {
  if (!fileUrl || typeof fileUrl !== 'string') {
    return Promise.reject(new Error('请提供有效的文件URL'))
  }
  
  return request.delete('/api/admin/cos/delete', {
    params: {
      arg0: fileUrl
    }
  })
}

